/**
 * useScaledDpr — the render resolution an editor's <Canvas dpr> should use.
 *
 * The adaptive budget's `renderScale` (the weak-GPU lever) has to be applied
 * through the Canvas PROP, never through an imperative setDpr(): R3F re-asserts
 * the prop on every Canvas render, so anything set behind its back gets
 * reverted, and each revert + re-apply reallocates the drawing buffer. That
 * ping-pong blanked the canvas for a frame at a time — the whole-editor flash
 * users hit in Perf/Auto mode (never in Quality, where the two values agreed).
 *
 * Passing a resolved NUMBER (not a [min, max] range) is what keeps it quiet:
 * R3F compares the prop against the live viewport dpr, so an unchanged number
 * is a no-op, while a range is re-resolved and re-applied every time.
 */

import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { getRenderScale, subscribeRenderScale } from './adaptiveBudget';

/** The live adaptive render scale (1 = native), re-rendering on change. */
export function useRenderScale(): number {
  return useSyncExternalStore(subscribeRenderScale, getRenderScale, getRenderScale);
}

/** window.devicePixelRatio, kept current across zoom / monitor changes. */
function useDeviceDpr(): number {
  const [dpr, setDpr] = useState(() => (typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1));
  useEffect(() => {
    const read = () => setDpr(window.devicePixelRatio || 1);
    // resolution media queries fire on zoom / monitor moves; resize covers the rest.
    const mq = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    mq.addEventListener?.('change', read);
    window.addEventListener('resize', read);
    return () => { mq.removeEventListener?.('change', read); window.removeEventListener('resize', read); };
  }, [dpr]);
  return dpr;
}

/**
 * Resolve an editor's base dpr (a number, or a [min, max] range like R3F's own)
 * and multiply it by the adaptive render scale.
 */
export function useScaledDpr(base: number | [number, number], dprCap?: number): number {
  const device = useDeviceDpr();
  const scale = useRenderScale();
  const baseMin = Array.isArray(base) ? base[0] : base;
  const baseMax = Array.isArray(base) ? base[1] : base;
  return useMemo(() => {
    let resolved = Array.isArray(base) ? Math.min(Math.max(baseMin, device), baseMax) : baseMin;
    // Potato pins the ratio BEFORE scaling: a 1.25dpr laptop panel renders at
    // 1× first, then the render scale applies on top.
    if (dprCap != null) resolved = Math.min(resolved, dprCap);
    // Round so float noise can't look like a change and trigger a resize.
    return Math.max(0.4, Math.round(resolved * scale * 1000) / 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseMin, baseMax, device, scale, dprCap]);
}
